import { supabase } from './supabase';
import { visitHoursOn } from './visitHours.js';
import { visitsFromResponse } from './axiscareVisits.js';

const callAxisCare = async (endpoint, options = {}) => {
  try {
    const { data, error } = await supabase.functions.invoke('axiscare-proxy', {
      body: { endpoint, ...options },
    });
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error('AxisCare API error:', err);
    return { data: null, error: err };
  }
};

// ── Visits ──────────────────────────────────────────
export const getVisits = (params = {}) => callAxisCare('/visits', { params });

// Raw AxisCare pull — internal. Every page goes through getVisitsByDateRange
// below, which merges in CAERA's own visits for any service line whose
// source of truth has been switched to 'caera' in the Service Catalog.
export const getAxisCareVisitsByDateRange = (startDate, endDate) =>
  callAxisCare('/visits', { params: { startDate, endDate } });

// ── Unified visit source (AxisCare + CAERA-native) ──────────────────────────
// The cut-over switch is per service line (service_catalog.visit_source).
// For a line still on 'axiscare', AxisCare's visits pass through untouched.
// For a line on 'caera', AxisCare's copies of that line are dropped and
// CAERA's native visits are emitted in the same shape AxisCare uses, so the
// eleven consumers (billing, payroll, revenue, reports, dashboard, schedule,
// visits, hours) keep working without knowing which system a visit came from.
//
// mode: 'all'     every native visit, clock times = actual (schedule views)
//       'payroll' verified native visits only, clock times = actual worked
//       'billing' verified native visits only, clock times = billable window
let catalogCache = { at: 0, rows: [] };
const getVisitSourceCatalog = async () => {
  if (Date.now() - catalogCache.at < 60_000) return catalogCache.rows;
  const { data } = await supabase.from('service_catalog')
    .select('id, code, name, procedure_code, modifier1, axiscare_service_code, axiscare_service_aliases, visit_source, default_charge_rate, active');
  catalogCache = { at: Date.now(), rows: data || [] };
  return catalogCache.rows;
};

const NATIVE_VISIT_SELECT = `
  id, evv_visit_id, scheduled_start, scheduled_end, actual_start, actual_end, billable_start, billable_end, billable_units,
  status, evv_status, evv_method, timezone, charge_rate, verified_at, modification_reason, service_id,
  client:clients ( id, client_id, axiscare_client_id, first_name, last_name ),
  staff:staff ( id, axiscare_id, first_name, last_name, full_name ),
  service:service_catalog ( id, code, name, procedure_code, modifier1, axiscare_service_code, default_charge_rate, payable )
`;

// Office verification does not gate money (decided 2026-09-13). The desk was
// the gate, but nothing has ever been verified through it — making it a hard
// requirement would empty the first billing run of a switched line rather than
// catch anything. `verified` still rides on every visit so a run can show how
// many of its lines nobody has reviewed, and the approval step on a billing run
// is where a second pair of eyes belongs. What money does depend on is the
// clock: payroll pays a clock pair (see visitHoursOn), billing bills the basis
// its run was created with.
const nativeToAxisShape = (v, mode) => {
  const billing = mode === 'billing';
  const inTime  = billing ? (v.billable_start || v.actual_start) : v.actual_start;
  const outTime = billing ? (v.billable_end   || v.actual_end)   : v.actual_end;
  const cancelled = v.status === 'cancelled';
  const c = v.client || {}; const s = v.staff; const svc = v.service || {};
  return {
    id: v.evv_visit_id, visitId: v.evv_visit_id, type: 'Hourly',
    client: { id: c.axiscare_client_id ?? `caera:${c.id}`, firstName: c.first_name, lastName: c.last_name, externalId: c.client_id, caeraId: c.id },
    caregiver: (s && !cancelled) ? { id: s.axiscare_id ?? `caera:${s.id}`, firstName: s.first_name, lastName: s.last_name, externalId: null, staffId: s.id } : null,
    startDate: v.scheduled_start, endDate: v.scheduled_end,
    scheduledStartDate: v.scheduled_start, scheduledEndDate: v.scheduled_end,
    actualStartDate: inTime || null, actualEndDate: outTime || null,
    clockIn:  inTime  ? { time: inTime,  method: v.evv_method === 'gps' ? 'Mobile' : 'Web', location: null, coordinates: null, telephonyPhone: null } : null,
    clockOut: outTime ? { time: outTime, method: v.evv_method === 'gps' ? 'Mobile' : 'Web', location: null, coordinates: null, telephonyPhone: null } : null,
    service: { id: svc.id, code: svc.axiscare_service_code || svc.code, description: svc.name, procedureCode: svc.procedure_code, modifier: svc.modifier1 || null },
    chargeRate: v.charge_rate ?? svc.default_charge_rate ?? null, billableRateMode: 'auto',
    payable: svc.payable !== false,
    removed: v.status === 'missed' || cancelled,
    verified: !!v.verified_at, timezone: v.timezone, modificationReason: v.modification_reason || null,
    _source: 'caera',
    _caera: { visitId: v.id, status: v.status, evvStatus: v.evv_status, billableStart: v.billable_start, billableEnd: v.billable_end, billableUnits: v.billable_units },
  };
};

export const getVisitsByDateRange = async (startDate, endDate, opts = {}) => {
  const mode = opts.mode || 'all';
  const [catalog, axis, native] = await Promise.all([
    getVisitSourceCatalog(),
    getAxisCareVisitsByDateRange(startDate, endDate),
    // CAERA's own visits only. The `source='axiscare'` rows in this table are a
    // one-off import, frozen since 5 September 2026; AxisCare itself, read
    // live above, is the source of truth for those visits. Reading the import
    // here too would put a stale copy beside the live one the day a line
    // switches to CAERA — the same care twice.
    supabase.from('visits').select(NATIVE_VISIT_SELECT)
      .eq('source', 'caera')
      .gte('scheduled_start', `${startDate}T00:00:00`).lte('scheduled_start', `${endDate}T23:59:59`)
      .order('scheduled_start'),
  ]);
  const caeraLines = catalog.filter(c => c.visit_source === 'caera');
  const stillOnAxis = catalog.some(c => c.active && c.visit_source !== 'caera');
  // While any line still depends on AxisCare, an AxisCare failure is a failure —
  // a billing or payroll run must never quietly go out half-empty.
  if (axis.error && (stillOnAxis || caeraLines.length === 0)) return axis;
  if (native.error && caeraLines.length > 0) return { data: null, error: native.error };

  // Every AxisCare label a switched line has ever been sent under, not just its
  // primary code. Three of the five billable lines carry aliases (Skilled-RN,
  // Unskilled-CNA, Unskilled-Family) and billingCodes.js already resolves on
  // them; matching only the primary code here would let an aliased AxisCare
  // visit survive alongside CAERA's own copy of the same care — billed twice.
  const caeraAxisCodes = new Set(
    caeraLines.flatMap(c => [c.axiscare_service_code, ...(c.axiscare_service_aliases || [])]).filter(Boolean)
  );
  const caeraIds = new Set(caeraLines.map(c => c.id));
  const rawAxis = axis.data?.results?.visits;
  const axisArr = visitsFromResponse(rawAxis)
    .filter(v => !caeraAxisCodes.has(v.service?.code))
    .map(v => ({ ...v, visitId: v.visitId ?? v.id, _source: 'axiscare' }));
  // Payroll pays hours worked on a payable line. A supervisory visit is on
  // the calendar, is completed, and is not paid per visit — the supervising
  // nurse's role covers it (decided 2026-10-01, after three of them, stamped
  // weeks long by a late report, were $66.5k of one run). It still appears
  // for billing and display; `billable` keeps it off the claim.
  const nativeArr = (native.data || [])
    .filter(v => caeraIds.has(v.service_id))
    .filter(v => mode !== 'payroll' || v.service?.payable !== false)
    .map(v => nativeToAxisShape(v, mode)).filter(Boolean);

  return {
    data: { results: { visits: [...axisArr, ...nativeArr] }, success: true,
            _sources: { axiscare: axisArr.length, caera: nativeArr.length, mode } },
    error: null,
  };
};

export const getTodayVisits = () => {
  const today = new Date().toISOString().split('T')[0];
  return getVisitsByDateRange(today, today);
};
export const addVisit = (visitData) => callAxisCare('/visits', { method: 'POST', body: visitData });
export const updateVisit = (visitId, data) => callAxisCare(`/visits/${visitId}`, { method: 'PATCH', body: data });

// ── Schedules ────────────────────────────────────────
export const getSchedules = (params = {}) => callAxisCare('/schedules', { params });
export const addSchedule = (data) => callAxisCare('/schedules', { method: 'POST', body: data });
export const updateSchedule = (id, data) => callAxisCare(`/schedules/${id}`, { method: 'PATCH', body: data });
export const removeSchedule = (id) => callAxisCare(`/schedules/${id}`, { method: 'DELETE' });

// ── Caregivers ───────────────────────────────────────
export const getCaregivers = () => callAxisCare('/caregivers');
export const getCaregiver = (id) => callAxisCare(`/caregivers/${id}`);
export const addCaregiver = (data) => callAxisCare('/caregivers', { method: 'POST', body: data });
export const updateCaregiver = (id, data) => callAxisCare(`/caregivers/${id}`, { method: 'PATCH', body: data });

// ── Clients ──────────────────────────────────────────
export const getAxisCareClients = () => callAxisCare('/clients');
export const getAxisCareClient = (id) => callAxisCare(`/clients/${id}`);
export const addAxisCareClient = (data) => callAxisCare('/clients', { method: 'POST', body: data });
export const updateAxisCareClient = (id, data) => callAxisCare(`/clients/${id}`, { method: 'PATCH', body: data });

// AxisCare's /clients response shape isn't pinned down anywhere else in the
// app (only /visits' results.visits wrapper is established) — handle the
// common envelope shapes defensively, same pattern used for visit lists.
export const parseAxisCareClients = (raw) => {
  const r = raw?.results?.clients ?? raw?.results ?? raw;
  if (!r) return [];
  return Array.isArray(r) ? r : Object.values(r);
};

export const normalizeClientName = (f, l) => `${f || ''} ${l || ''}`.replace(/\s+/g, ' ').trim().toLowerCase();

// ── Notes ────────────────────────────────────────────
export const getNotes = (entityType, entityId) =>
  callAxisCare('/notes', { params: { entity_type: entityType, entity_id: entityId } });
export const createNote = (data) => callAxisCare('/notes', { method: 'POST', body: data });

// ── Tasks ────────────────────────────────────────────
export const getAxisCareTasks = () => callAxisCare('/tasks');

// ── Helpers ──────────────────────────────────────────
export const getMissedVisits = async (startDate, endDate) => {
  const now = new Date();
  const start = startDate || new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const end   = endDate   || now.toISOString().split('T')[0];
  const { data, error } = await getVisitsByDateRange(start, end);
  if (error || !data?.results?.visits) return { data: [], error };
  const raw = Array.isArray(data.results.visits) ? data.results.visits : Object.values(data.results.visits);
  // Build set of active visit keys (client + date) to exclude reschedules
  const activeKeys = new Set(
    raw.filter(v => !v.removed).map(v => `${v.client?.id}|${(v.scheduledStartDate || v.startDate || '').slice(0,10)}`)
  );
  // Truly missed = removed AND had a caregiver AND no active replacement same day
  // Unassigned removed visits are open/cancelled shifts, not missed visits
  const missed = raw.filter(v => {
    if (!v.removed) return false;
    if (!v.caregiver) return false; // skip unassigned — not a missed visit
    const key = `${v.client?.id}|${(v.scheduledStartDate || v.startDate || '').slice(0,10)}`;
    return !activeKeys.has(key);
  });
  return { data: missed, error: null };
};

// Helper: convert AxisCare visits object/array to clean array
export const parseVisits = (raw) => {
  if (!raw) return [];
  return Array.isArray(raw) ? raw : Object.values(raw);
};

// Client and caregiver names on a visit. They live in axiscareVisits.js so
// the Monday payroll job reads them the same way.
export { visitClientName, visitCaregiverName } from './axiscareVisits.js';

// Helper: get visit status
export const visitStatus = (v) => v.removed ? 'Missed' : v.clockIn ? 'Completed' : 'Scheduled';

// Helper: calc visit hours (scheduled — used for scheduling/authorization views)
export const visitHours = (v) => {
  const s = v.scheduledStartDate || v.startDate;
  const e = v.scheduledEndDate   || v.endDate;
  if (!s || !e) return 0;
  const h = (new Date(e) - new Date(s)) / 3600000;
  return isNaN(h) || h < 0 ? 0 : h;
};

// ── Hours basis ─────────────────────────────────────────────────────────────
// The rule lives in lib/visitHours.js, which has no Supabase import so edge
// functions can take it through _shared/lib. Re-exported here for the callers
// that have always imported it from this file.
export { HOURS_BASES, hasClockPair, visitHoursOn, clockWindow } from './visitHours.js';

// Kept for the display surfaces that already read it — Dashboard's delivered
// hours, the Calendar drawer, the month grid. Its meaning is unchanged: clocked
// where present, scheduled where not. Money paths name their basis explicitly
// through visitHoursOn instead.
export const visitActualHours = (v) => visitHoursOn(v, 'clocked_else_scheduled');

export const getHoursThisMonth = async () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const end = now.toISOString().split('T')[0];
  return getVisitsByDateRange(start, end);
};
